import { AddressType } from "@1games-ru/region-shared/dist/enums/address/address";
import { Position } from "@1games-ru/region-shared/dist/types/world/world";
import { useCallback, useEffect, useState } from "react";
import CreateEntityMpProvider from "@/core/data-provider/data-mp/create-entity/create-entity-mp-provider";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";

export type AddressPoint = {
  id: number;
  streetId: number;
  streetName: string;
  type: AddressType | null;
  index: number;
  position: Position;
};

export default function usePointsByAddressId(addressId: number | null) {
  const [isPending, setIsPending] = useState(false);
  const [data, setData] = useState<AddressPoint[] | null>(null);

  const getPointsByAddressId = useCallback(async () => {
    if (addressId === null) return;

    setIsPending(true);

    const response = await CreateEntityMpProvider.getAddressPoints({
      streetIds: addressId !== null ? [addressId] : null,
      streetName: null,
      pointIds: null,
      types: null,
    });

    if (response instanceof RpcMpProviderFailedResponse) {
      setIsPending(false);
      console.error(response);
      throw new Error("getAddressStreets - CreateEntityMpProvider.getAddressStreets() error");
    }

    setIsPending(false);

    setData(
      response.data
        .map((point) => ({
          id: point.pointId,
          streetId: point.streetId,
          streetName: point.streetName,
          type: point.pointType,
          index: point.index,
          position: point.position,
        }))
        .sort((a, b) => b.index - a.index),
    );
  }, [addressId]);

  useEffect(() => {
    if (addressId !== null) {
      getPointsByAddressId();
    }
  }, [addressId, getPointsByAddressId]);

  const refetch = useCallback(async () => {
    await getPointsByAddressId();
  }, [getPointsByAddressId]);

  const reset = useCallback(() => {
    setData(null);
  }, []);

  return { data, isPending, refetch, reset };
}
